import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled, { css } from 'styled-components';
import { Link } from '@helpcenter/analytics-components/src/link';
import { isIsomorphicDompurifyLibUsed } from '@helpcenter/feature-flags';
import type { SearchProps } from '@helpcenter/result-base';
import * as fonts from '@helpcenter/styles/src/fonts';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import * as transitions from '@helpcenter/styles/src/transitions';
import { sanitize } from 'dompurify';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import DocumentIcon from '@atlaskit/icon/glyph/media-services/document';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { addSourceToUrl } from '@atlassian/help-center-common-util/article-util';
import { addParamsToUrl } from '@atlassian/help-center-common-util/history-qs';
import { isThirdPartyArticle, urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import GoogleDriveIcon from '@atlassian/third-party-connectors/src/common/ui/icons/google-drive-icon';
import SharepointIcon from '@atlassian/third-party-connectors/src/common/ui/icons/sharepoint-icon';
import type { SourceType } from 'rest/search/kbs/types';
interface KbSearchProps extends SearchProps {
    titleHtml: string;
    to: string;
    actionSubjectId: string;
    snippet?: string;
    openNewTab?: boolean;
    isSuggestion?: boolean;
    showPortalName?: boolean;
    portalName?: string;
    sourceType?: SourceType;
}

const KbSnippet: React.FC<KbSearchProps> = ({
    titleHtml,
    to,
    actionSubjectId,
    openNewTab,
    onClick,
    snippet,
    queryHash,
    portalName,
    sourceType,
    showPortalName = false,
    ...props
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const isAnExternalResource = isThirdPartyArticle(sourceType);
    const linkProps = FeatureGates.checkGate('3p-integration-enabled_t5ysh')
        ? urlRefsAndTarget({
              isAnExternalResource,
              displayLink: addSourceToUrl(to, 'request-form', isAnExternalResource),
          })
        : {};

    const getIcon = () => {
        if (!FeatureGates.checkGate('3p-integration-enabled_t5ysh')) {
            return <DocumentIcon label="" size="medium" />;
        }
        if (sourceType === 'GOOGLE_DRIVE') {
            return <GoogleDriveIcon label="" size="medium" />;
        }
        if (sourceType === 'SHAREPOINT') {
            return <SharepointIcon label="" size="medium" />;
        }
        return <DocumentIcon label="" size="medium" />;
    };

    React.useEffect(() => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'viewed',
        }).fire();
    }, [createAnalyticsEvent]);

    return (
        <LinkContainer
            to={
                !FeatureGates.checkGate('3p-integration-enabled_t5ysh')
                    ? addParamsToUrl(to, { src: queryHash })
                    : undefined
            }
            key={to}
            onClick={onClick}
            actionSubjectId={actionSubjectId}
            target={openNewTab ? '_blank' : '_self'}
            data-test-id="article-link-container"
            {...props}
            {...linkProps}
        >
            {getIcon()}
            <ArticleContainer snippets={snippet}>
                <Title
                    dangerouslySetInnerHTML={{
                        __html: isIsomorphicDompurifyLibUsed() ? isomorphicSanitize(titleHtml) : sanitize(titleHtml),
                    }}
                />
                {showPortalName && portalName && <ServiceDeskTitle>from {portalName}</ServiceDeskTitle>}
                {snippet && (
                    <Snippet
                        dangerouslySetInnerHTML={{
                            __html: isIsomorphicDompurifyLibUsed() ? isomorphicSanitize(snippet) : sanitize(snippet),
                        }}
                    />
                )}
            </ArticleContainer>
        </LinkContainer>
    );
};
export default withAnalyticsContext({ componentName: 'KbSnippet' })(KbSnippet);

interface LinkContainerProps {
    isSuggestion?: boolean;
}

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TitleH3Css = css`
    ${fonts.regular};
    overflow: hidden;
    text-overflow: ellipsis;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 1;
    word-break: break-word;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    text-transform: uppercase;
    margin: ${token('space.0', '0px')};
    font-weight: normal;
    transition: color ${transitions.speedMs.medium}ms ${transitions.timingFunctions.easeInOut};
    color: ${token('color.text', colors.N800)};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Title = styled.h3`
    ${TitleH3Css}
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const SnippetParagraphCss = css`
    ${fonts.small};
    color: ${token('color.text', colors.N800)};
    margin-top: ${token('space.100', '8px')};
    padding-right: ${token('space.200', '16px')};
    overflow: hidden;
    text-overflow: ellipsis;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    line-height: 20px;
    word-break: break-word;
    white-space: normal;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Snippet = styled.p`
    ${SnippetParagraphCss}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LinkContainer = styled(Link)<LinkContainerProps>`
    ${mixins.borderRadius};
    ${(props) => mixins.applyIf(!props.isSuggestion, mixins.raiseUpOnHoverOrFocus())};
    display: flex;
    background-color: ${(props) => (props.isSuggestion ? 'none' : token('elevation.surface.raised', colors.N0))};
    padding-top: ${token('space.200', '16px')};
    padding-right: ${token('space.050', '4px')};
    padding-bottom: ${token('space.200', '16px')};
    padding-left: ${token('space.150', '12px')};
    margin: ${(props) => (props.isSuggestion ? '0px' : `${token('space.100', '8px')} 0 ${token('space.200', '16px')}`)};
    &:hover,
    &:focus {
        background: ${(props) =>
            props.isSuggestion
                ? token('color.background.neutral.subtle.hovered', colors.N30)
                : token('elevation.surface.raised', colors.N0)};
        text-decoration: none;
    }
    &:hover h3,
    &:focus h3 {
        color: ${(props) => token('color.link', props.theme.color)};
    }
    ${(props) => {
        /*
         * We have to overwrite the styles that ReactRenderer from '@atlaskit/renderer'
         * is applying. ReactRenderer is used in the Proforma packages.
         * */
        // eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
        return css`
        #proforma-form-create-app & {
        h3 {
            ${TitleH3Css}
            color: ${token('color.text', colors.N800)};
            line-height: ${grid.multiple(2.5).px};
        }
        p {
            ${SnippetParagraphCss}
        }
        &:hover,
        &:focus {
            background: ${
                props.isSuggestion
                    ? token('color.background.discovery.hovered', colors.N30)
                    : token('elevation.surface.raised', colors.N0)
            }
            text-decoration: none;
        }
        &:hover h3,
        &:focus h3 {
            color: ${token('color.link', props.theme.color)};
        }
    }
    `;
    }}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ArticleContainer = styled.div<{ snippets?: string }>`
    margin-left: ${token('space.250', '20px')};
    ${(props) =>
        !props.snippets
            ? `
            display: flex;
            align-items: center
            `
            : ''}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ServiceDeskTitle = styled.div`
    color: ${token('color.text.subtlest', colors.N100)};
    font-size: ${grid.multiple(1.375).px};
`;
